import { useEffect, useRef, useState, type ReactElement } from 'react'
import { useLocation, useOutlet } from 'react-router-dom'

export const PAGE_TRANSITION_MS = 400

type PageView = {
  key: string
  outlet: ReactElement | null
}

type PageTransitionOutletProps = {
  className?: string
}

export function PageTransitionOutlet({
  className = '',
}: PageTransitionOutletProps) {
  const location = useLocation()
  const outlet = useOutlet()
  const [view, setView] = useState<PageView>(() => ({
    key: location.key,
    outlet,
  }))
  const [phase, setPhase] = useState<'enter' | 'exit'>('enter')
  const pendingView = useRef<PageView>({ key: location.key, outlet })
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    pendingView.current = { key: location.key, outlet }

    if (location.key === view.key) {
      return
    }

    setPhase('exit')

    timerRef.current = window.setTimeout(() => {
      setView(pendingView.current)
      setPhase('enter')
      timerRef.current = null
    }, PAGE_TRANSITION_MS)

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current)
      }
    }
  }, [location.key, outlet, view.key])

  return (
    <div
      className={[
        'page-transition',
        phase === 'exit' ? 'page-transition--exit' : 'page-transition--enter',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {view.outlet}
    </div>
  )
}
